import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search, Plus, Calendar, User, Edit, RefreshCw,
  AlertCircle, Loader2, Phone, X,
  FileText, UserPlus, ChevronDown, Stethoscope,
  FilterIcon, UserCheck, UserX, LayoutList, CalendarDays, Columns3,
} from 'lucide-react';
import { format, startOfWeek, addDays } from 'date-fns';
import { getAppointmentsByDateRange, updateAppointment, queueCheckin } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import EditAppointmentModal from '@/components/Appointment/EditAppointmentModal';
import AddPatientForm from '@/components/Patient/AddPatientForm';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import AppointmentCalendar from '@/components/Appointment/AppointmentCalendar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import type { PatientSearchResult } from '@/hooks/usePatientSearch';

type ViewMode = 'list' | 'day' | 'week';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Patient { id: string; name: string; age?: number; gender?: string; phone?: string }

interface Appointment {
  id: string; patient_id: string; date: string; time: string;
  reason: string; status: string; doctor_id?: string;
  // inline patient fields returned by API
  patient_name?: string; patient_age?: number;
  patient_gender?: string; patient_phone?: string;
}

interface AptRow extends Appointment {
  patientName: string; patientAge: number;
  patientGender: string; patientPhone: string;
  aptDate: Date; isToday: boolean; isPast: boolean; timeDiff: number;
}

interface Filters {
  dateRange: 'today' | 'tomorrow' | 'week' | 'month' | 'custom';
  customStart: string; customEnd: string;
  status: string; search: string;
}

type ConfirmAction = { id: string; action: 'cancel' | 'no_show' };
type Modal = 'none' | 'appointment' | 'newPatient' | 'editAppointment';

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  scheduled:  { label: 'Scheduled',  color: 'text-primary',      bg: 'bg-primary/10',     dot: 'bg-primary'      },
  completed:  { label: 'Completed',  color: 'text-success',      bg: 'bg-success/10',     dot: 'bg-success'      },
  cancelled:  { label: 'Cancelled',  color: 'text-destructive',  bg: 'bg-destructive/10', dot: 'bg-destructive/60'},
  checked_in: { label: 'Checked In', color: 'text-warning',      bg: 'bg-warning/10',     dot: 'bg-warning'      },
  no_show:    { label: 'No Show',    color: 'text-destructive',  bg: 'bg-destructive/10', dot: 'bg-destructive/60'},
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtTime(t: string) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const d = new Date(); d.setHours(h, m);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtRemaining(diff: number) {
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function loadRange(f: Filters): { start: string; end: string } {
  const todayStr = new Date().toISOString().split('T')[0];
  if (f.dateRange === 'custom' && f.customStart && f.customEnd) {
    return { start: f.customStart, end: f.customEnd };
  }
  // Pre-defined ranges: load today → today+90 so client-side filters always have data
  const end = new Date(); end.setDate(end.getDate() + 90);
  return { start: todayStr, end: end.toISOString().split('T')[0] };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AppointmentsPage() {
  useEffect(() => { document.title = 'Appointments | OneCheckUp'; }, []);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // Front-desk view if user has no clinical profile (doctor_id), regardless of role
  const isFrontDesk = !currentUser?.doctor_id;

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modal, setModal] = useState<Modal>('none');
  const [editingApt, setEditingApt] = useState<AptRow | null>(null);
  const [newPatientForApt, setNewPatientForApt] = useState<Patient | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState('');

  const [filters, setFilters] = useState<Filters>({
    dateRange: 'today', customStart: '', customEnd: '',
    status: 'all', search: '',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [newAptPrefill, setNewAptPrefill] = useState<{ date?: string; time?: string }>({});

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadData = useCallback(async (f: Filters) => {
    if (!currentUser?.clinic_id) return;
    try {
      const { start, end } = loadRange(f);
      const doctorFilter = currentUser.doctor_id ?? undefined;
      const data = await getAppointmentsByDateRange(start, end, currentUser.clinic_id, doctorFilter);
      if (data) {
        setAppointments(
          data.sort((a: Appointment, b: Appointment) => {
            const da = new Date(`${a.date.split('T')[0]} ${a.time}`);
            const db = new Date(`${b.date.split('T')[0]} ${b.time}`);
            return da.getTime() - db.getTime();
          })
        );
      }
      setError(null);
    } catch {
      setError('Failed to load appointments.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUser?.clinic_id, currentUser?.role, currentUser?.doctor_id]);

  useEffect(() => { loadData(filters); }, [loadData]);

  useEffect(() => {
    if (searchParams.get('filter') === 'today')
      setFilters(prev => ({ ...prev, dateRange: 'today' }));
  }, [searchParams]);

  // Re-fetch when switching to custom range with both dates set
  useEffect(() => {
    if (filters.dateRange === 'custom' && filters.customStart && filters.customEnd) {
      setRefreshing(true);
      loadData(filters);
    }
  }, [filters.customStart, filters.customEnd, loadData]);

  // Re-fetch when calendar navigates to a new day/week
  useEffect(() => {
    if (viewMode === 'list' || !currentUser?.clinic_id) return;
    const doctorFilter = currentUser.doctor_id ?? undefined;
    let start: string, end: string;
    if (viewMode === 'day') {
      start = end = format(calendarDate, 'yyyy-MM-dd');
    } else {
      const ws = startOfWeek(calendarDate, { weekStartsOn: 1 });
      start = format(ws, 'yyyy-MM-dd');
      end   = format(addDays(ws, 6), 'yyyy-MM-dd');
    }
    setRefreshing(true);
    getAppointmentsByDateRange(start, end, currentUser.clinic_id, doctorFilter)
      .then(data => { if (data) setAppointments(data); })
      .catch(() => {})
      .finally(() => setRefreshing(false));
  }, [calendarDate, viewMode, currentUser?.clinic_id]);

  const handleRefresh = () => {
    setRefreshing(true);
    if (viewMode === 'list') {
      loadData(filters);
    } else {
      // Re-trigger the calendar fetch effect by nudging calendarDate
      setCalendarDate(d => new Date(d));
    }
  };

  // ── Parsing ─────────────────────────────────────────────────────────────────

  const parse = useCallback((apt: Appointment): AptRow => {
    const dateOnly = apt.date.split('T')[0];
    const aptDateTime = new Date(`${dateOnly} ${apt.time}`);
    const now = new Date();
    const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
    const aptDate = new Date(dateOnly + 'T00:00:00');
    return {
      ...apt,
      patientName:   apt.patient_name   || 'Unknown',
      patientAge:    apt.patient_age    || 0,
      patientGender: apt.patient_gender || '',
      patientPhone:  apt.patient_phone  || '',
      aptDate,
      isToday: aptDate.toDateString() === todayMidnight.toDateString(),
      isPast:  aptDateTime < now,
      timeDiff: aptDateTime.getTime() - now.getTime(),
    };
  }, []);

  // ── Filtering ───────────────────────────────────────────────────────────────

  const filtered = useMemo<AptRow[]>(() => {
    let list = appointments.map(parse);
    const today = new Date(); today.setHours(0, 0, 0, 0);

    switch (filters.dateRange) {
      case 'today':
        list = list.filter(a => a.isToday); break;
      case 'tomorrow': {
        const tmr = new Date(today); tmr.setDate(tmr.getDate() + 1);
        list = list.filter(a => a.aptDate.toDateString() === tmr.toDateString()); break;
      }
      case 'week': {
        const wEnd = new Date(today); wEnd.setDate(wEnd.getDate() + 7);
        list = list.filter(a => a.aptDate >= today && a.aptDate <= wEnd); break;
      }
      case 'month': {
        const mEnd = new Date(today); mEnd.setMonth(mEnd.getMonth() + 1);
        list = list.filter(a => a.aptDate >= today && a.aptDate <= mEnd); break;
      }
      case 'custom':
        if (filters.customStart && filters.customEnd) {
          const s = new Date(filters.customStart + 'T00:00:00');
          const e = new Date(filters.customEnd + 'T23:59:59');
          list = list.filter(a => a.aptDate >= s && a.aptDate <= e);
        }
        break;
    }

    if (filters.status !== 'all') list = list.filter(a => a.status === filters.status);

    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      list = list.filter(a =>
        a.patientName.toLowerCase().includes(q) ||
        a.patientPhone.includes(q) ||
        (a.reason && a.reason.toLowerCase().includes(q))
      );
    }

    return list;
  }, [appointments, filters, parse]);

  // ── Stats — based on the current filtered view ───────────────────────────────

  const stats = useMemo(() => ({
    total:      filtered.length,
    scheduled:  filtered.filter(a => a.status === 'scheduled').length,
    completed:  filtered.filter(a => a.status === 'completed').length,
    cancelled:  filtered.filter(a => a.status === 'cancelled' || a.status === 'no_show').length,
  }), [filtered]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const executeCancel = async (apt: AptRow) => {
    setConfirmAction(null);
    try {
      await updateAppointment({ ...apt, status: 'cancelled', updated_at: new Date().toISOString() });
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, status: 'cancelled' } : a));
      toast({ title: 'Cancelled', description: `${apt.patientName}'s appointment cancelled` });
    } catch {
      toast({ title: 'Error', description: 'Failed to cancel', variant: 'destructive' });
    }
  };

  const executeNoShow = async (apt: AptRow) => {
    setConfirmAction(null);
    try {
      await updateAppointment({ ...apt, status: 'no_show', updated_at: new Date().toISOString() });
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, status: 'no_show' } : a));
      toast({ title: 'Marked as no-show', description: apt.patientName });
    } catch {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const handleCheckIn = async (apt: AptRow | Appointment) => {
    setCheckingInId(apt.id);
    try {
      const entry = await queueCheckin(apt.id);
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, status: 'checked_in' } : a));
      toast({
        title: 'Checked in',
        description: (() => { const n = (apt as AptRow).patientName ?? apt.patient_name ?? ''; return entry ? `Token #${entry.tokenNumber} · ${n}` : n; })(),
      });
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Check-in failed', variant: 'destructive' });
    } finally {
      setCheckingInId(null);
    }
  };

  // ── Modal helpers ────────────────────────────────────────────────────────────

  const closeModal = useCallback(() => {
    setModal('none');
    setEditingApt(null);
    setSelectedPatientId('');
    setNewPatientForApt(null);
    setNewAptPrefill({});
  }, []);

  const handlePatientSelected = useCallback((p: PatientSearchResult) => {
    setSelectedPatientId(p.id);
  }, []);

  const handlePatientCreated = useCallback((p: Patient) => {
    setNewPatientForApt(p);
    setSelectedPatientId(p.id);
    setModal('appointment');
    toast({ title: 'Patient created', description: `${p.name} selected for appointment` });
  }, []);

  // ── Guards ───────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="w-7 h-7 text-primary animate-spin mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Loading appointments...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6 text-center py-12">
      <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
      <p className="text-muted-foreground mb-4">{error}</p>
      <Button onClick={() => loadData(filters)}>Try Again</Button>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="prescription-form-container">
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/10 rounded-xl">
            <Calendar className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Appointments</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {/* View toggle */}
          <div className="flex items-center bg-muted/40 rounded-lg p-1">
            {([
              { mode: 'list' as ViewMode, icon: LayoutList,   label: 'List' },
              { mode: 'day'  as ViewMode, icon: CalendarDays, label: 'Day'  },
              { mode: 'week' as ViewMode, icon: Columns3,     label: 'Week' },
            ] as const).map(({ mode, icon: Icon, label }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === mode
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => setModal('appointment')} className="gap-2">
            <Plus className="w-4 h-4" /> Add Appointment
          </Button>
        </div>
      </div>

      {/* Stats — list view only (calendar has its own navigation context) */}
      {viewMode === 'list' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total',               value: stats.total,     color: 'text-foreground'  },
            { label: 'Scheduled',           value: stats.scheduled, color: 'text-primary'     },
            { label: 'Completed',           value: stats.completed, color: 'text-success'     },
            { label: 'Cancelled / No-show', value: stats.cancelled, color: 'text-destructive' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-2.5 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters — list view only */}
      {viewMode === 'list' && (
      <div className="bg-card border border-border rounded-xl p-2.5 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">

          {/* Date range tabs */}
          <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1">
            {(['today', 'tomorrow', 'week', 'month'] as const).map(r => (
              <button
                key={r}
                onClick={() => setFilters(prev => ({ ...prev, dateRange: r }))}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  filters.dateRange === r
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 relative min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search patient or phone..."
              value={filters.search}
              onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-8 pr-3 py-2 text-sm border border-input rounded-lg bg-background focus:outline-none focus:border-ring"
            />
          </div>

          {/* Status filter */}
          <select
            value={filters.status}
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="text-sm border border-input rounded-lg px-3 py-2 bg-background focus:outline-none focus:border-ring"
          >
            <option value="all">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="checked_in">Checked In</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No Show</option>
          </select>

          {/* Advanced toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${
              showAdvanced ? 'border-primary text-primary bg-primary/5' : 'border-input text-muted-foreground hover:text-foreground'
            }`}
          >
            <FilterIcon className="w-3.5 h-3.5" />
            Custom
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Custom date range */}
        {showAdvanced && (
          <div className="pt-2 border-t border-border flex items-center gap-3 flex-wrap">
            <span className="text-xs text-muted-foreground">Date range:</span>
            <input
              type="date"
              value={filters.customStart}
              onChange={e => setFilters(prev => ({ ...prev, customStart: e.target.value, dateRange: 'custom' }))}
              className="text-xs border border-input rounded-lg px-2 py-2 bg-background"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date"
              value={filters.customEnd}
              onChange={e => setFilters(prev => ({ ...prev, customEnd: e.target.value, dateRange: 'custom' }))}
              className="text-xs border border-input rounded-lg px-2 py-2 bg-background"
            />
            <button
              onClick={() => {
                setFilters({ dateRange: 'today', customStart: '', customEnd: '', status: 'all', search: '' });
                setShowAdvanced(false);
              }}
              className="text-xs text-muted-foreground hover:text-foreground ml-auto"
            >
              Clear
            </button>
          </div>
        )}
      </div>
      )}

      {/* Calendar view */}
      {viewMode !== 'list' && (
        <AppointmentCalendar
          view={viewMode}
          date={calendarDate}
          appointments={appointments}
          isFrontDesk={isFrontDesk}
          checkingInId={checkingInId}
          onNavigate={setCalendarDate}
          onNewAppointment={(date, time) => {
            setNewAptPrefill({ date, time });
            setModal('appointment');
          }}
          onEditAppointment={apt => {
            setEditingApt(parse(apt as Appointment));
            setModal('editAppointment');
          }}
          onCheckIn={apt => handleCheckIn(apt as Appointment)}
        />
      )}

      {/* Results count — list view only */}
      {viewMode === 'list' && (
        <p className="text-xs text-muted-foreground px-1">
          {filtered.length} appointment{filtered.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* List */}
      {viewMode === 'list' && (filtered.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Calendar className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No appointments found</p>
          <p className="text-xs text-muted-foreground/60 mt-1 mb-4">
            {filters.search || filters.status !== 'all'
              ? 'Try adjusting your filters'
              : 'No appointments scheduled for this period'}
          </p>
          <Button size="sm" onClick={() => setModal('appointment')} className="gap-2">
            <Plus className="w-4 h-4" /> Schedule Appointment
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(apt => {
            const cfg = STATUS[apt.status] ?? STATUS.scheduled;
            const remaining = apt.isToday && apt.status === 'scheduled' && !apt.isPast
              ? fmtRemaining(apt.timeDiff) : null;
            const isUrgent = remaining !== null && apt.timeDiff <= 900_000;
            const isConfirming = confirmAction?.id === apt.id;

            return (
              <div
                key={apt.id}
                className={`bg-card border rounded-xl p-3 transition-all hover:shadow-sm ${
                  isUrgent ? 'border-warning/40 bg-warning/5' : 'border-border'
                } ${apt.status === 'cancelled' || apt.status === 'no_show' ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center gap-4">

                  {/* Time */}
                  <div className="text-center w-20 flex-shrink-0">
                    <p className="text-sm font-bold text-foreground leading-none">{fmtTime(apt.time)}</p>
                    {filters.dateRange !== 'today' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {apt.aptDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </p>
                    )}
                  </div>

                  <div className="w-px bg-border flex-shrink-0 self-stretch" />

                  {/* Patient */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-foreground capitalize">{apt.patientName}</p>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {cfg.label}
                      </span>
                      {remaining && (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          isUrgent ? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'
                        }`}>
                          {remaining} remaining
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      {(apt.patientAge || apt.patientGender) && (
                        <span>
                          {[apt.patientAge ? `${apt.patientAge}y` : null, apt.patientGender || null].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {apt.patientPhone && (
                        <a href={`tel:${apt.patientPhone}`} onClick={e => e.stopPropagation()}
                           className="flex items-center gap-1 hover:text-primary transition-colors">
                          <Phone className="w-3.5 h-3.5" /> {apt.patientPhone}
                        </a>
                      )}
                      {apt.reason && <span className="truncate max-w-[200px]">{apt.reason}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isConfirming ? (
                      /* Inline confirm */
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-destructive font-medium">
                          {confirmAction.action === 'cancel' ? 'Cancel appointment?' : 'Mark as no-show?'}
                        </span>
                        <button
                          onClick={() => confirmAction.action === 'cancel' ? executeCancel(apt) : executeNoShow(apt)}
                          className="px-2.5 py-1 rounded text-xs font-semibold bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmAction(null)}
                          className="px-2.5 py-1 rounded text-xs font-semibold bg-muted text-muted-foreground hover:bg-muted/80"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* Consult — doctor only */}
                        {apt.status === 'scheduled' && !apt.isPast && !isFrontDesk && (
                          <Button
                            size="sm"
                            onClick={() => navigate(`/prescriptions?patient=${apt.patient_id}&appointment=${apt.id}&mode=consultation`)}
                            className="gap-1.5 h-8 text-xs mr-1"
                          >
                            <Stethoscope className="w-3.5 h-3.5" /> Consult
                          </Button>
                        )}

                        {/* Check In — frontdesk only */}
                        {apt.status === 'scheduled' && isFrontDesk && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5 h-8 text-xs bg-success/5 border-success/30 text-success hover:bg-success/10"
                            disabled={checkingInId === apt.id}
                            onClick={() => handleCheckIn(apt)}
                          >
                            {checkingInId === apt.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <UserCheck className="w-3.5 h-3.5" />
                            }
                            Check In
                          </Button>
                        )}

                        {/* View patient */}
                        <button
                          onClick={() => navigate(isFrontDesk ? `/frontdesk/patients/${apt.patient_id}` : `/patients/${apt.patient_id}`)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          title="View patient"
                        >
                          <User className="w-4 h-4" />
                        </button>

                        {/* Prescriptions — doctor only */}
                        {!isFrontDesk && (
                          <button
                            onClick={() => navigate(`/prescriptions?patient=${apt.patient_id}`)}
                            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            title="View prescriptions"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        )}

                        {/* Edit */}
                        {apt.status === 'scheduled' && (
                          <button
                            onClick={() => { setEditingApt(apt); setModal('editAppointment'); }}
                            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            title="Edit appointment"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}

                        {/* No Show — frontdesk, scheduled */}
                        {apt.status === 'scheduled' && isFrontDesk && (
                          <button
                            onClick={() => setConfirmAction({ id: apt.id, action: 'no_show' })}
                            className="p-2 rounded-lg text-muted-foreground hover:text-warning hover:bg-warning/10 transition-colors"
                            title="Mark as no-show"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}

                        {/* Cancel */}
                        {apt.status === 'scheduled' && (
                          <button
                            onClick={() => setConfirmAction({ id: apt.id, action: 'cancel' })}
                            className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title="Cancel appointment"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Modals */}
      {modal === 'appointment' && (
        <AddAppointmentModal
          isOpen
          onClose={closeModal}
          patientId={selectedPatientId}
          patients={newPatientForApt ? [newPatientForApt as any] : []}
          showPatientSelection
          onPatientSelected={handlePatientSelected}
          onCreateNewPatient={() => setModal('newPatient')}
          onAdded={() => { loadData(filters); closeModal(); }}
          initialDate={newAptPrefill.date}
          initialTime={newAptPrefill.time}
        />
      )}

      {modal === 'newPatient' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                <h2 className="text-base font-semibold">New Patient</h2>
              </div>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <AddPatientForm onSuccess={handlePatientCreated} onCancel={closeModal} compact />
            </div>
          </div>
        </div>
      )}

      {modal === 'editAppointment' && editingApt && (
        <EditAppointmentModal
          appointment={editingApt}
          isOpen
          onClose={closeModal}
          onUpdated={updated => {
            setAppointments(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a));
            closeModal();
            toast({ title: 'Updated', description: 'Appointment updated' });
          }}
        />
      )}
    </div>
    </div>
  );
}
